import { useEffect, useRef } from 'react';

interface TouchOptions {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onSwipeUp?: () => void;
  onSwipeDown?: () => void;
}

/**
 * Hook to ensure touch events work properly on kiosk
 * Adds both click and touch handlers to prevent issues with touch screens
 */
export function useTouch(options: TouchOptions = {}) {
  const touchStartX = useRef<number>(0);
  const touchStartY = useRef<number>(0);
  const touchEndX = useRef<number>(0);
  const touchEndY = useRef<number>(0);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.changedTouches[0].screenX;
    touchStartY.current = e.changedTouches[0].screenY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    touchEndX.current = e.changedTouches[0].screenX;
    touchEndY.current = e.changedTouches[0].screenY;
    checkSwipeDirection();
  };

  const checkSwipeDirection = () => {
    const deltaX = touchEndX.current - touchStartX.current;
    const deltaY = touchEndY.current - touchStartY.current;
    const minSwipeDistance = 50;

    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      // Horizontal swipe
      if (Math.abs(deltaX) > minSwipeDistance) {
        if (deltaX > 0 && options.onSwipeRight) {
          options.onSwipeRight();
        } else if (deltaX < 0 && options.onSwipeLeft) {
          options.onSwipeLeft();
        }
      }
    } else {
      // Vertical swipe
      if (Math.abs(deltaY) > minSwipeDistance) {
        if (deltaY > 0 && options.onSwipeDown) {
          options.onSwipeDown();
        } else if (deltaY < 0 && options.onSwipeUp) {
          options.onSwipeUp();
        }
      }
    }
  };

  useEffect(() => {
    let isUsingTouch = false;
    
    // Add global touch event listener to ensure touch works
    const handleGlobalTouch = (e: TouchEvent) => {
      const target = e.target as HTMLElement;

      // Mark that we're using touch
      if (!isUsingTouch) {
        isUsingTouch = true;
        // Only hide cursor when actually using touch
        document.body.style.cursor = 'none';
      }

      // Check if this element or any ancestor has its own touch handlers
      // by looking for data-has-touch attribute or onTouchEnd handler
      let el: HTMLElement | null = target;
      while (el) {
        if (el.getAttribute('data-has-touch') === 'true' || el.ontouchend) {
          // Don't interfere with elements that have their own touch handling
          console.log('[useTouch] Skipping - element has own touch handler');
          return;
        }
        el = el.parentElement;
      }

      // If it's a button or clickable element, trigger click
      if (target && (
        target.tagName === 'BUTTON' ||
        target.tagName === 'A' ||
        target.classList.contains('clickable') ||
        target.getAttribute('role') === 'button' ||
        target.onclick
      )) {
        e.preventDefault();
        // Create and dispatch a click event
        const clickEvent = new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window
        });
        target.dispatchEvent(clickEvent);
      }
    };
    
    // Show cursor when mouse is moved
    const handleMouseMove = () => {
      if (isUsingTouch) {
        isUsingTouch = false;
        document.body.style.cursor = 'auto';
      }
    };

    // Only add on touch devices
    if ('ontouchstart' in window) {
      document.addEventListener('touchstart', handleGlobalTouch, { passive: false });
      document.addEventListener('mousemove', handleMouseMove);
      
      return () => {
        document.removeEventListener('touchstart', handleGlobalTouch);
        document.removeEventListener('mousemove', handleMouseMove);
        document.body.style.cursor = 'auto'; // Reset on cleanup
      };
    }
  }, []);

  return { handleTouchStart, handleTouchEnd };
}

/**
 * Helper to create touch-friendly click handlers
 */
export function useTouchClick(handler: () => void) {
  return {
    onClick: handler,
    onTouchEnd: (e: React.TouchEvent) => {
      e.preventDefault();
      handler();
    }
  };
}